import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Select,
  MenuItem,
  Box,
  FormHelperText,
  Typography,
  Autocomplete,
  Chip,
  LinearProgress,
} from '@mui/material';
import { useParams } from 'react-router';
import { usePackagerUpdate } from '../../hooks/packager/usePackagerUpdate';
import {
  PackagerScheduleTypes,
  DocumentDataStatusFilters,
} from '../../../types/packager-types';
import { packagerFormDirtyAtom } from '../../../atoms/packager-atoms';
import { useAtom } from 'jotai';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import { FormControlStyled, FormLabelStyled } from '../../common/Dialog/DialogGlynt';
import useGetPackager from '../../hooks/packager/useGetPackager';
import { formatEnumToLabel } from '../../../utils/formatEnumToLabel';
import PackagerConfigUnsavedChanges from './PackagerConfigUnsavedChanges';
import PackagerCronSuggestions from './PackagerCronSuggestions';
import cronstrue from 'cronstrue';
import { describeCronSchedule } from '../../../utils/cronNextRun';

interface PackagerStep1PackagerProps {
  updatePackagerMutation: ReturnType<typeof usePackagerUpdate>;
  handleCloseConfirmation: () => void;
  handlePrevious: () => void;
  handleNext: (skipGuard?: boolean) => void;
  simulateMode?: boolean;
  draftChanges?: Partial<import('../../../types/packager-types').Packager>;
  setDraftChanges?: (changes: Partial<import('../../../types/packager-types').Packager>) => void;
  triggerSimulation?: () => void;
}

interface FormData {
  packager_schedule_type: PackagerScheduleTypes;
  schedule_cron_expression: string;
  packaging_begin_date: string;
  packaging_end_date: string;
  coc_report_start_date: string;
  coc_report_end_date: string;
  document_data_status_filter: DocumentDataStatusFilters[];
  document_id_filter: string[];
  eb_id_filter: string[];
}

const SCHEDULE_TYPE_VALUES = Object.values(PackagerScheduleTypes);
const DOCUMENT_DATA_STATUS_VALUES = Object.values(DocumentDataStatusFilters);

const PackagerStep1Packager: React.FC<PackagerStep1PackagerProps> = ({
  updatePackagerMutation,
  handleCloseConfirmation,
  handlePrevious,
  handleNext,
  simulateMode = false,
  draftChanges = {},
  setDraftChanges,
  triggerSimulation,
}) => {
  const { dpid } = useParams<{ dpid: string }>();
  const { packager, isLoading } = useGetPackager(dpid!);
  const [showUnsavedDialog, setShowUnsavedDialog] = React.useState(false);
  const [pendingCloseAction, setPendingCloseAction] = React.useState<(() => void) | null>(null);
  const [showCronSuggestions, setShowCronSuggestions] = React.useState(false);
  const [formDirty, setFormDirty] = useAtom(packagerFormDirtyAtom);
  const [documentIdInputDraft, setDocumentIdInputDraft] = React.useState('');
  const [ebIdInputDraft, setEbIdInputDraft] = React.useState('');

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    setValue,
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      packager_schedule_type: packager?.packager_schedule_type || PackagerScheduleTypes.MANUAL,
      schedule_cron_expression: packager?.schedule_cron_expression || '',
      packaging_begin_date: packager?.packaging_begin_date || '',
      packaging_end_date: packager?.packaging_end_date || '',
      coc_report_start_date: packager?.coc_report_start_date || '',
      coc_report_end_date: packager?.coc_report_end_date || '',
      document_data_status_filter: packager?.document_data_status_filter || [],
      document_id_filter: packager?.document_id_filter || [],
      eb_id_filter: packager?.eb_id_filter || [],
    },
    mode: 'onChange',
  });

  React.useEffect(() => {
    if (packager) {
      setValue(
        'packager_schedule_type',
        packager.packager_schedule_type || PackagerScheduleTypes.MANUAL
      );
      setValue('schedule_cron_expression', packager.schedule_cron_expression || '');
      setValue('packaging_begin_date', packager.packaging_begin_date || '');
      setValue('packaging_end_date', packager.packaging_end_date || '');
      setValue('coc_report_start_date', packager.coc_report_start_date || '');
      setValue('coc_report_end_date', packager.coc_report_end_date || '');
      setValue('document_data_status_filter', packager.document_data_status_filter || []);
      setValue('document_id_filter', packager.document_id_filter || []);
      setValue('eb_id_filter', packager.eb_id_filter || []);
      setDocumentIdInputDraft('');
      setEbIdInputDraft('');
    }
  }, [packager, setValue]);

  React.useEffect(() => {
    if (!simulateMode) return;
    if (draftChanges.packager_schedule_type !== undefined)
      setValue('packager_schedule_type', draftChanges.packager_schedule_type as PackagerScheduleTypes);
    if (draftChanges.schedule_cron_expression !== undefined)
      setValue('schedule_cron_expression', (draftChanges.schedule_cron_expression as string) || '');
    if (draftChanges.packaging_begin_date !== undefined)
      setValue('packaging_begin_date', (draftChanges.packaging_begin_date as string) || '');
    if (draftChanges.packaging_end_date !== undefined)
      setValue('packaging_end_date', (draftChanges.packaging_end_date as string) || '');
    if (draftChanges.coc_report_start_date !== undefined)
      setValue('coc_report_start_date', (draftChanges.coc_report_start_date as string) || '');
    if (draftChanges.coc_report_end_date !== undefined)
      setValue('coc_report_end_date', (draftChanges.coc_report_end_date as string) || '');
    if (draftChanges.document_data_status_filter)
      setValue('document_data_status_filter', draftChanges.document_data_status_filter);
    if (draftChanges.document_id_filter)
      setValue('document_id_filter', draftChanges.document_id_filter);
    if (draftChanges.eb_id_filter) setValue('eb_id_filter', draftChanges.eb_id_filter);
  }, [simulateMode, draftChanges, setValue]);

  const hasUncommittedInput =
    Boolean(documentIdInputDraft.trim()) || Boolean(ebIdInputDraft.trim());

  React.useEffect(() => {
    setFormDirty(isDirty || hasUncommittedInput);
  }, [isDirty, hasUncommittedInput, setFormDirty]);

  const onSubmit = (data: FormData) => {
    if (!packager?.id) return;

    const payload = {
      packager_schedule_type: data.packager_schedule_type,
      schedule_cron_expression: data.schedule_cron_expression,
      packaging_begin_date: data.packaging_begin_date || null,
      packaging_end_date: data.packaging_end_date || null,
      coc_report_start_date: data.coc_report_start_date || null,
      coc_report_end_date: data.coc_report_end_date || null,
      document_data_status_filter: data.document_data_status_filter,
      document_id_filter: data.document_id_filter,
      eb_id_filter: data.eb_id_filter,
    };

    if (simulateMode && setDraftChanges) {
      setDraftChanges({ ...draftChanges, ...payload });
      setFormDirty(false);
      handleNext(true);
    } else {
      updatePackagerMutation.mutate(
        { dataPoolId: dpid!, packagerId: packager.id, data: payload },
        {
          onSuccess: () => handleNext(),
          onError: (error) => console.error('Mutation failed:', error),
        }
      );
    }
  };

  const handleFormSubmit = () => {
    if (isDirty) {
      handleSubmit(onSubmit)();
    } else if (hasUncommittedInput) {
      setPendingCloseAction(() => handleNext);
      setShowUnsavedDialog(true);
    } else {
      handleNext();
    }
  };

  const handleCloseWithCheck = (closeAction: () => void) => {
    if (formDirty) {
      setPendingCloseAction(() => closeAction);
      setShowUnsavedDialog(true);
    } else {
      closeAction();
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedDialog(false);
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
  };

  const handleCancelClose = () => {
    setShowUnsavedDialog(false);
    setPendingCloseAction(null);
  };

  if (isLoading || !packager) {
    return <LinearProgress />;
  }

  return (
    <>
      <DialogContentGlynt paddingTop="0" scrollable>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>
          Schedule
        </Typography>

        <FormControlStyled fullWidth>
          <FormLabelStyled>SCHEDULE TYPE</FormLabelStyled>
          <Controller
            name="packager_schedule_type"
            control={control}
            rules={{ required: 'Schedule type is required' }}
            render={({ field }) => (
              <Select
                {...field}
                variant="outlined"
                size="small"
                data-cy="schedule-type-select"
                error={!!errors.packager_schedule_type}
              >
                {SCHEDULE_TYPE_VALUES.map((value) => (
                  <MenuItem
                    key={value}
                    value={value}
                    data-cy={`schedule-type-${value.toLowerCase()}`}
                  >
                    {formatEnumToLabel(value)}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {errors.packager_schedule_type && (
            <FormHelperText error>{errors.packager_schedule_type.message}</FormHelperText>
          )}
        </FormControlStyled>

        {watch('packager_schedule_type') === PackagerScheduleTypes.CRON_SCHEDULE && (
          <FormControlStyled fullWidth>
            <FormLabelStyled>CRON EXPRESSION</FormLabelStyled>
            <Typography variant="body2" sx={{ marginBottom: '8px', color: 'text.secondary' }}>
              Enter a cron expression to schedule when the packager should run. Times are in UTC.
            </Typography>

            <Box
              sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Need help with cron expressions?
              </Typography>
              <Box
                component="button"
                type="button"
                onClick={() => setShowCronSuggestions(!showCronSuggestions)}
                sx={{
                  background: 'none',
                  border: '1px solid #1976d2',
                  borderRadius: 1,
                  px: 2,
                  py: 0.5,
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  color: '#1976d2',
                  '&:hover': { backgroundColor: '#f3f8ff' },
                }}
              >
                {showCronSuggestions ? 'Hide Suggestions' : 'Show Suggestions'}
              </Box>
            </Box>

            {showCronSuggestions && (
              <PackagerCronSuggestions
                onSelectExpression={(expr) => {
                  setValue('schedule_cron_expression', expr);
                  trigger('schedule_cron_expression');
                }}
              />
            )}
            <Controller
              name="schedule_cron_expression"
              control={control}
              rules={{
                required: 'Cron expression is required when using CRON_SCHEDULE',
                validate: (value) => {
                  if (!value) return 'Cron expression is required';
                  const parts = value.trim().split(/\s+/);
                  if (parts.length !== 5) {
                    return 'Cron expression must have exactly 5 parts: minutes hours day-of-month month day-of-week';
                  }
                  try {
                    cronstrue.toString(value);
                    return true;
                  } catch (error) {
                    return 'Invalid cron expression. Please check your syntax.';
                  }
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="0 17 * * *"
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={!!errors.schedule_cron_expression}
                  helperText={errors.schedule_cron_expression?.message}
                  data-cy="cron-expression-input"
                />
              )}
            />

            {watch('schedule_cron_expression') && !errors.schedule_cron_expression && (
              <FormHelperText sx={{ color: 'success.main', mt: 1 }}>
                {(() => {
                  try {
                    return describeCronSchedule(watch('schedule_cron_expression'));
                  } catch (error) {
                    return 'Invalid cron expression';
                  }
                })()}
              </FormHelperText>
            )}
          </FormControlStyled>
        )}

        <FormControlStyled fullWidth>
          <FormLabelStyled>PACKAGING DATE RANGE</FormLabelStyled>
          <Typography variant="body2" sx={{ marginBottom: '8px', color: 'text.secondary' }}>
            Narrows the document set by created_at.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Controller
              name="packaging_begin_date"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                  data-cy="packaging-begin-date-input"
                />
              )}
            />
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              -
            </Typography>
            <Controller
              name="packaging_end_date"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                  data-cy="packaging-end-date-input"
                />
              )}
            />
          </Box>
        </FormControlStyled>

        <FormControlStyled fullWidth>
          <FormLabelStyled>CHAIN OF CUSTODY DATE RANGE</FormLabelStyled>
          <Typography variant="body2" sx={{ marginBottom: '8px', color: 'text.secondary' }}>
            Pre-populates the Chain of Custody report modal only; does not affect packaging.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Controller
              name="coc_report_start_date"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                  data-cy="coc-report-start-date-input"
                />
              )}
            />
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              -
            </Typography>
            <Controller
              name="coc_report_end_date"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                  data-cy="coc-report-end-date-input"
                />
              )}
            />
          </Box>
        </FormControlStyled>

        <hr style={{ borderTop: '0.5px solid #BDC3D2', marginTop: '20px' }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
          Filters
        </Typography>
        <Typography variant="body2" sx={{ marginBottom: '8px', color: 'text.secondary' }}>
          The packaging date range narrows results. Within that range, documents matching the
          status filter below are included.
        </Typography>

        <FormControlStyled fullWidth>
          <FormLabelStyled>DOCUMENT DATA STATUS FILTER</FormLabelStyled>
          <Controller
            name="document_data_status_filter"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={DOCUMENT_DATA_STATUS_VALUES}
                getOptionLabel={(option) => formatEnumToLabel(option)}
                value={field.value}
                onChange={(_, newValue) => field.onChange(newValue)}
                data-cy="document-status-filter-select"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    data-cy="document-status-filter-input"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={formatEnumToLabel(option)}
                      size="small"
                      data-cy={`document-status-chip-${option.toLowerCase()}`}
                    />
                  ))
                }
              />
            )}
          />
        </FormControlStyled>

        <hr style={{ borderTop: '0.5px solid #BDC3D2', marginTop: '20px' }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
          Always Include
        </Typography>
        <Typography variant="body2" sx={{ marginBottom: '8px', color: 'text.secondary' }}>
          Documents or extraction batches listed here are always included in the package,
          regardless of the date range or filters above.
        </Typography>

        <FormControlStyled fullWidth>
          <FormLabelStyled>DOCUMENT IDS</FormLabelStyled>
          <Typography variant="body2" sx={{ marginBottom: '8px', color: 'text.secondary' }}>
            Hit enter after entering text.
          </Typography>
          <Controller
            name="document_id_filter"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                getOptionLabel={(option) => option}
                value={field.value}
                onChange={(_, newValue) => {
                  field.onChange(newValue);
                  setDocumentIdInputDraft('');
                }}
                inputValue={documentIdInputDraft}
                onInputChange={(_, newInputValue) => {
                  setDocumentIdInputDraft(newInputValue);
                }}
                data-cy="document-ids-input"
                renderInput={(params) => <TextField {...params} variant="outlined" size="small" />}
              />
            )}
          />
          {Boolean(documentIdInputDraft.trim()) && (
            <Typography
              variant="caption"
              sx={{ color: 'error.main', marginTop: '4px', display: 'block' }}
            >
              Press Enter to add the value; otherwise it won't be saved.
            </Typography>
          )}
        </FormControlStyled>

        <FormControlStyled fullWidth>
          <FormLabelStyled>EXTRACTION BATCH IDS</FormLabelStyled>
          <Typography variant="body2" sx={{ marginBottom: '8px', color: 'text.secondary' }}>
            Hit enter after entering text.
          </Typography>
          <Controller
            name="eb_id_filter"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                getOptionLabel={(option) => option}
                value={field.value}
                onChange={(_, newValue) => {
                  field.onChange(newValue);
                  setEbIdInputDraft('');
                }}
                inputValue={ebIdInputDraft}
                onInputChange={(_, newInputValue) => {
                  setEbIdInputDraft(newInputValue);
                }}
                data-cy="extraction-batch-ids-input"
                renderInput={(params) => <TextField {...params} variant="outlined" size="small" />}
              />
            )}
          />
          {Boolean(ebIdInputDraft.trim()) && (
            <Typography
              variant="caption"
              sx={{ color: 'error.main', marginTop: '4px', display: 'block' }}
            >
              Press Enter to add the value; otherwise it won't be saved.
            </Typography>
          )}
        </FormControlStyled>
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleFormSubmit}
        closeModal={() => handleCloseWithCheck(handleCloseConfirmation)}
        cancelCypressId="btn-packager-config-step1-modal-cancel"
        submitCypressId="btn-packager-config-step1-modal-submit"
        clearCypressId="btn-packager-config-step1-modal-clear"
        showClearButton={false}
        submitButtonText={simulateMode ? 'Next' : 'Save & Next'}
        submitLoading={simulateMode ? false : updatePackagerMutation.isPending}
        submitDisabled={!isValid || (simulateMode ? false : updatePackagerMutation.isPending)}
      />
      <PackagerConfigUnsavedChanges
        open={showUnsavedDialog}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
      />
    </>
  );
};

export default PackagerStep1Packager;
